import { AnalyticIdentifier } from '@common/neo.tax';
import { colors, InputFormContainer, Stack, ToggleSwitch, Typography } from '@ui-components/neo.tax';
import { ReactElement } from 'react';

import { useAscContext } from '@neo.tax/asc/context';
import { useUpdateAscProjectCapitalizableProjectClusteringOverride } from '@neo.tax/asc/hooks';
import { computeCapitalizableProjectClusteringStatus, isNoiseOrNoRootClusterType } from '@neo.tax/asc/utils';
import {
  AscClusterType,
  CapitalizableProjectClusteringOverride,
  ToastConfigState,
  useAnalyticId,
  useCurrentCompanyOrFail,
} from '@neo.tax/shared';

export type CapitalizableProjectClustering = {
  id: string;
  capitalizableProjectClusteringOverride?: CapitalizableProjectClusteringOverride | null;
  capitalizationStartDate?: Date | string | null;
  capitalizationEndDate?: Date | string | null;
  clusterType?: AscClusterType | null;
  startDate?: Date | string | null;
  // Last activity date — gates clause 2 (recent activity required to stay a matching target).
  endDate?: Date | string | null;
};

export const CapitalizableProjectClusteringSection = ({
  ascProject,
}: {
  ascProject: CapitalizableProjectClustering;
}): ReactElement => {
  const inputId = useAnalyticId(AnalyticIdentifier.ASC_PROJECT_DETAIL_CAPITALIZABLE_PROJECT_CLUSTERING_OVERRIDE);
  const { fiscalYearId, periodName } = useAscContext();
  const company = useCurrentCompanyOrFail();
  const clusteringMethod = company.syncConfigurationAsc?.clusteringMethod ?? null;
  const [updateOverride, { loading }] = useUpdateAscProjectCapitalizableProjectClusteringOverride();
  const isNoise = isNoiseOrNoRootClusterType(ascProject.clusterType);
  const status = computeCapitalizableProjectClusteringStatus(
    ascProject,
    clusteringMethod,
    company.lastCompletedAscSyncPeriodEnd,
  );

  // If flipping the switch lands on what the default would already produce,
  // drop the override entirely so the project keeps tracking capitalization changes.
  const defaultActive = computeCapitalizableProjectClusteringStatus(
    { ...ascProject, capitalizableProjectClusteringOverride: null },
    clusteringMethod,
    company.lastCompletedAscSyncPeriodEnd,
  ).isActive;

  const handleToggle = (checked: boolean): void => {
    const override =
      checked === defaultActive
        ? null
        : checked
          ? CapitalizableProjectClusteringOverride.Included
          : CapitalizableProjectClusteringOverride.Excluded;
    void (async (): Promise<void> => {
      await updateOverride({
        variables: {
          companyId: company.id,
          ascProjectId: ascProject.id,
          updateAscProjectCapitalizableProjectClusteringOverrideInput: { override },
          fiscalYearId,
          periodName,
        },
        context: {
          errorToast: { title: 'Failed to update matching target override' },
        },
      });
      ToastConfigState.pushToast({ title: 'Matching target override updated' });
    })();
  };

  return (
    <InputFormContainer
      inputId={inputId}
      label={
        <Stack direction="column" gap={0.5}>
          <Typography as="label" fontSize="sm" fontWeight="medium" color={colors.gray[700]}>
            Next Sync Matching
          </Typography>
          <Typography fontSize="xs" color={colors.gray[400]}>
            Match capitalizable tickets to this project on next sync.
          </Typography>
        </Stack>
      }
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={6} paddingX={3} paddingY={1}>
        {isNoise ? (
          <Typography fontSize="xs" color={colors.gray[500]}>
            Not editable — miscellaneous/ops projects do not match tickets.
          </Typography>
        ) : (
          <>
            <Stack direction="column" gap={0.5}>
              <Typography fontSize="xs" color={colors.gray[500]}>
                Status
              </Typography>
              <Typography fontSize="sm" color={colors.gray[900]}>
                {status.isActive ? 'Active' : 'Inactive'}
              </Typography>
              <Typography fontSize="xs" color={colors.gray[500]}>
                {status.reasonText}
              </Typography>
            </Stack>
            <ToggleSwitch id={inputId} checked={status.isActive} disabled={loading} onChange={handleToggle} />
          </>
        )}
      </Stack>
    </InputFormContainer>
  );
};
